import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import UpdateNotification from "@/components/UpdateNotification";

// Lazy-loaded pages - only loaded when the user navigates to them
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TripDetail = lazy(() => import("@/pages/TripDetail"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const SharedTrip = lazy(() => import("@/pages/SharedTrip"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ProfileSettings = lazy(() => import("@/pages/ProfileSettings"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const TravelAnalytics = lazy(() => import("@/pages/TravelAnalytics"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const MyJournal = lazy(() => import("@/pages/MyJournal"));

// Shared loading spinner for Suspense boundaries
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full" />
  </div>
);

// Hook to handle iOS PWA standalone mode safe area
const useIOSSafeArea = () => {
  useEffect(() => {
    // Check if running as iOS PWA (standalone mode)
    const isIOSPWA = window.navigator.standalone === true;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOSPWA || (isIOS && window.matchMedia('(display-mode: standalone)').matches)) {
      // Add a class to the document for iOS PWA styling
      document.documentElement.classList.add('ios-pwa-standalone');
      
      // Create and inject a style element for PWA-specific styles
      const style = document.createElement('style');
      style.id = 'ios-pwa-safe-area-styles';
      style.textContent = `
        .ios-pwa-standalone body {
          padding-top: 50px !important;
        }
        .ios-pwa-standalone .fixed.top-0 {
          top: 50px !important;
        }
      `;
      document.head.appendChild(style);
      
      console.log('[App] iOS PWA standalone mode detected, applying safe area padding');
    }
    
    return () => {
      // Cleanup
      const style = document.getElementById('ios-pwa-safe-area-styles');
      if (style) style.remove();
      document.documentElement.classList.remove('ios-pwa-standalone');
    };
  }, []);
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Check if there's a referral code - if so, ALWAYS show the auth page
  // so users can see what new signups will see
  const searchParams = new URLSearchParams(location.search);
  const hasRefCode = searchParams.has('ref');
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full" />
      </div>
    );
  }
  
  // If there's a ref code, ALWAYS show the signup page (even if logged in)
  // This allows users to see what the signup page looks like with their referral code
  if (hasRefCode) {
    return children;
  }
  
  // If authenticated and no ref code, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Check if there's a referral code in the URL - if so, always show landing page
  const searchParams = new URLSearchParams(location.search);
  const hasRefCode = searchParams.has('ref');
  
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Landing page for non-authenticated users */}
      <Route path="/welcome" element={
        isAuthenticated ? <Navigate to="/" replace /> : <LandingPage />
      } />
      <Route path="/auth" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPassword />
        </PublicRoute>
      } />
      <Route path="/" element={
        loading ? (
          <PageLoader />
        ) : (isAuthenticated && !hasRefCode) ? (
          <Dashboard />
        ) : (
          <LandingPage />
        )
      } />
      <Route path="/trip/:tripId" element={
        <ProtectedRoute>
          <TripDetail />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfileSettings />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <ProfileSettings />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <TravelAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/my-journal" element={
        <ProtectedRoute>
          <MyJournal />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      <Route path="/shared/:shareToken" element={<SharedTrip />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/legal/:page" element={<LegalPage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  // Apply iOS PWA safe area handling
  useIOSSafeArea();
  
  return (
    <div className="App min-h-screen bg-background">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="bottom-right" richColors />
      <UpdateNotification />
    </div>
  );
}

export default App;
